import React, { useCallback, useContext, useEffect, useState } from 'react';

import Modal from '../../components/Modal';
import { ApiContext } from '../../contexts/ApiContext';
import ProjectTableDetail from './ProjectTableDetail';
import { fmtDate } from '../../utils/ruDates';

const EMPTY_FORM = { name: '', description: '', ai_description: '' };

function ProjectTablesPanel({ workspaceId, canEdit }) {
    const { request, showStatus } = useContext(ApiContext);

    const [loading, setLoading] = useState(false);
    const [tables, setTables] = useState([]);
    const [activeTableId, setActiveTableId] = useState(null);

    const [formModalOpen, setFormModalOpen] = useState(false);
    const [editingTableId, setEditingTableId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);

    const loadTables = useCallback(async () => {
        if (!workspaceId) return;
        setLoading(true);
        try {
            const data = await request(`/api/v1/workspaces/${workspaceId}/project-tables`);
            const list = data.tables || [];
            setTables(list);
            setActiveTableId((prev) => {
                if (prev && list.some((t) => t.id === prev)) return prev;
                return list[0]?.id || null;
            });
        } catch (error) {
            showStatus('error', error.message || 'Не удалось загрузить таблицы проекта.');
        } finally {
            setLoading(false);
        }
    }, [workspaceId, request, showStatus]);

    useEffect(() => {
        loadTables();
    }, [loadTables]);

    useEffect(() => {
        setActiveTableId(null);
    }, [workspaceId]);

    const openCreateModal = useCallback(() => {
        setEditingTableId(null);
        setForm(EMPTY_FORM);
        setFormModalOpen(true);
    }, []);

    const openEditModal = useCallback((table) => {
        setEditingTableId(table.id);
        setForm({
            name: table.name || '',
            description: table.description || '',
            ai_description: table.ai_description || '',
        });
        setFormModalOpen(true);
    }, []);

    const saveTable = useCallback(async () => {
        if (!form.name.trim()) {
            showStatus('error', 'Укажите название таблицы.');
            return;
        }
        try {
            if (editingTableId) {
                await request(`/api/v1/workspaces/${workspaceId}/project-tables/${editingTableId}`, {
                    method: 'PATCH',
                    body: JSON.stringify({
                        name: form.name.trim(),
                        description: form.description,
                        ai_description: form.ai_description,
                    }),
                });
                showStatus('success', 'Таблица обновлена.');
            } else {
                const created = await request(`/api/v1/workspaces/${workspaceId}/project-tables`, {
                    method: 'POST',
                    body: JSON.stringify({
                        name: form.name.trim(),
                        description: form.description,
                        ai_description: form.ai_description,
                        columns: [],
                    }),
                });
                showStatus('success', 'Таблица создана.');
                setActiveTableId(created.id);
            }
            setFormModalOpen(false);
            await loadTables();
        } catch (error) {
            showStatus('error', error.message || 'Не удалось сохранить таблицу.');
        }
    }, [form, editingTableId, request, workspaceId, showStatus, loadTables]);

    const deleteTable = useCallback(async (table) => {
        if (!window.confirm(`Удалить таблицу «${table.name}» вместе со всеми строками?`)) return;
        try {
            await request(`/api/v1/workspaces/${workspaceId}/project-tables/${table.id}`, { method: 'DELETE' });
            showStatus('success', 'Таблица удалена.');
            if (activeTableId === table.id) setActiveTableId(null);
            await loadTables();
        } catch (error) {
            showStatus('error', error.message || 'Не удалось удалить таблицу.');
        }
    }, [request, workspaceId, showStatus, loadTables, activeTableId]);

    const activeTable = tables.find((t) => t.id === activeTableId) || null;

    return (
        <div className="project-tables">
            <div className="project-tables__tabs">
                {tables.map((table) => (
                    <button
                        key={table.id}
                        type="button"
                        className={table.id === activeTableId ? 'active' : 'ghost'}
                        onClick={() => setActiveTableId(table.id)}
                        title={table.description || table.name}
                    >
                        {table.name}
                        <span className="muted"> · {table.row_count}</span>
                    </button>
                ))}
                {canEdit && (
                    <button type="button" className="ghost" onClick={openCreateModal} disabled={loading}>
                        + Создать таблицу
                    </button>
                )}
            </div>

            {!tables.length && !loading && (
                <div className="empty">
                    В проекте пока нет таблиц. {canEdit ? 'Создайте первую — например, «Ассортимент» или «Конкуренты».' : ''}
                </div>
            )}

            {activeTable && (
                <>
                    <div className="project-tables__meta">
                        <div className="project-tables__meta-info">
                            {activeTable.description && <p className="muted">{activeTable.description}</p>}
                            <p className="muted project-tables__meta-line">
                                Создано {fmtDate(activeTable.created_at)}
                                {activeTable.created_by_name ? ` · ${activeTable.created_by_name}` : ''}
                            </p>
                            {activeTable.ai_description && (
                                <p className="muted project-tables__ai-desc">
                                    🤖 Описание для AI: {activeTable.ai_description}
                                </p>
                            )}
                        </div>
                        {canEdit && (
                            <div className="project-tables__meta-actions">
                                <button type="button" className="ghost" onClick={() => openEditModal(activeTable)}>
                                    Настройки таблицы
                                </button>
                                <button type="button" className="ghost" onClick={() => deleteTable(activeTable)}>
                                    Удалить таблицу
                                </button>
                            </div>
                        )}
                    </div>

                    <ProjectTableDetail
                        workspaceId={workspaceId}
                        tableId={activeTable.id}
                        canEdit={canEdit && activeTable.can_edit}
                    />
                </>
            )}

            <Modal
                open={formModalOpen}
                onClose={() => setFormModalOpen(false)}
                title={editingTableId ? 'Настройки таблицы' : 'Новая таблица проекта'}
            >
                <div className="project-assortment__form">
                    <label>
                        <span>Название</span>
                        <input
                            type="text"
                            value={form.name}
                            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                            placeholder="Например: Ассортимент, Конкуренты, Поставщики"
                        />
                    </label>
                    <label>
                        <span>Описание (для людей)</span>
                        <textarea
                            rows={2}
                            value={form.description}
                            onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                            placeholder="Необязательно — что хранится в таблице."
                        />
                    </label>
                    <label>
                        <span>Описание для AI-агента</span>
                        <textarea
                            rows={3}
                            value={form.ai_description}
                            onChange={(event) => setForm((prev) => ({ ...prev, ai_description: event.target.value }))}
                            placeholder="Что это за таблица и как AI-агент должен её использовать (учитывается при ответах и при работе с данными проекта)."
                        />
                    </label>
                    <div className="project-assortment__actions">
                        <button type="button" className="primary" onClick={saveTable}>Сохранить</button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default ProjectTablesPanel;
